import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Text,
  BlockStack,
  TextField,
  Select,
  Popover,
  Checkbox,
  Button,
  InlineStack,
  DropZone,
  Spinner,
  Banner,
} from '@shopify/polaris';
import { generationImportService } from '../../services/generationImportService';
import { authService } from '../../services/authService';
import { getServerURL } from '../../services/config';
import { extractEnabledAiModelOptions } from '../../constants/aiModels';
import TextStyleEditor from '../text/TextStyleEditor';
import {
  buildTextStylePayload,
  normalizeTextStyle,
} from '../../utils/textStyleLayout';
import { montageProfilesService } from '../../services/montageProfilesService';
import {
  buildMontageLaneOptions,
  buildMontageProfileAssignments,
  buildMontageProfileOptions,
  getDefaultMontageProfileOutputs,
  getMontageLaneSelectors,
  resolveMontageProfileKey,
} from '../../utils/montageProfiles';
import AuthenticatedAssetImage from '../AuthenticatedAssetImage';
import { validateSpreadLayoutForm, buildLayoutPayload } from '../../utils/storyLayoutForm';

const COMFY_FLOW_OPTIONS = [
  { label: 'Dynamic Story (image + mask templates)', value: 'dynamic_story' },
  { label: 'Legacy Static (image + mask templates)', value: 'legacy_static' },
  { label: 'Prompt-based Scenes', value: 'prompt_story' },
];

const ASPECT_RATIO_PRESETS = [
  { label: '1:1 Square (square_hd - Default)', value: '1:1' },
  { label: '3:4 Portrait (portrait_4_3)', value: '3:4' },
  { label: '4:3 Landscape (landscape_4_3)', value: '4:3' },
  { label: '9:16 Story Vertical (portrait_16_9)', value: '9:16' },
  { label: '16:9 Widescreen (landscape_16_9)', value: '16:9' },
  { label: '2:1 Two-Page Spread', value: '2:1' },
  { label: 'Custom W:H ratio...', value: 'custom' },
];

const REGEN_PROVIDER_OPTIONS = [
  { label: 'Inherit global default', value: '' },
  { label: 'Vertex', value: 'vertex' },
  { label: 'ComfyUI', value: 'comfyui' },
  { label: 'FAL.AI', value: 'fal-ai' },
];

const normalizeProviderValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return '';
  if (normalized === 'vertex' || normalized === 'gemini') return 'vertex';
  if (normalized === 'comfyui' || normalized === 'flux2') return 'comfyui';
  if (normalized === 'fal-ai' || normalized === 'fal.ai' || normalized === 'fal-flux-max' || normalized === 'fal-ai-flux2-dev' || normalized === 'fal-flux2-dev' || normalized === 'flux2-dev') return 'fal-ai';
  return '';
};

const CreateGenerationStoryModal = ({
  isOpen,
  onClose,
  onStoryCreated
}) => {
  const [isCreatingGenerationStory, setIsCreatingGenerationStory] = useState(false);
  const [generationStoryAgePopoverActive, setGenerationStoryAgePopoverActive] = useState(false);
  const [availableFonts, setAvailableFonts] = useState([]);
  const [isLoadingFonts, setIsLoadingFonts] = useState(false);
  const [regenerationModelOptions, setRegenerationModelOptions] = useState([]);
  const [generationModelOptions, setGenerationModelOptions] = useState([]);
  const [storyCoverFile, setStoryCoverFile] = useState(null);
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [storyCoverPreviewUrl, setStoryCoverPreviewUrl] = useState(null);
  const [availableMontageProfiles, setAvailableMontageProfiles] = useState([]);

  const ageOptions = ['0-2', '2-4', '4-6', '6-8', '8-10'];

  const [newGenerationStoryData, setNewGenerationStoryData] = useState({
    slug: '',
    title: '',
    description: '',
    gender: 'boy',
    age: [],
    language: 'english',
    comfy_flow: 'dynamic_story',
    layout_mode: 'single',
    layout_aspect_ratio: '',
    layout_resolution: '2k',
    layout_wide_ratio: '',
    line_art: false,
    montage_profile_key: 'dynamic-story-v1',
    montage_profile_hard_cover: '',
    montage_profile_booklet: '',
    montage_profile_pristol_express: '',
    montage_profile_booklet_express: '',
    gift_prompt: '',
    use_text_default_style: false,
    text_style_form: normalizeTextStyle({}),
    layout_mode: 'single',
    layout_aspect_ratio: '1:1',
    layout_resolution: '1k',
    layout_wide_ratio: '',
    line_art: false,
    generation_provider_override: '',
    generation_model_override: '',
    regeneration_provider_override: '',
    regeneration_model_override: '',
    story_cover: ''
  });

  const [layoutError, setLayoutError] = useState('');

  const fontOptions = [
    { label: 'Language fallback font', value: '' },
    ...availableFonts.map((font) => ({
      label: `${font.label || font.family || font.key} (${font.key})`,
      value: font.key || ''
    }))
  ];

  const regenerationModelSelectOptions = [
    { label: 'Inherit global default', value: '' },
    ...(() => {
      const options = [...regenerationModelOptions];
      const currentValue = String(newGenerationStoryData.regeneration_model_override || '').trim();
      if (currentValue && !options.some((item) => item.value === currentValue)) {
        options.push({ label: currentValue, value: currentValue });
      }
      return options;
    })()
  ];

  const generationModelSelectOptions = [
    { label: 'Inherit global default', value: '' },
    ...(() => {
      const options = [...generationModelOptions];
      const currentValue = String(newGenerationStoryData.generation_model_override || '').trim();
      if (currentValue && !options.some((item) => item.value === currentValue)) {
        options.push({ label: currentValue, value: currentValue });
      }
      return options;
    })()
  ];

  // Helper function to generate slug from title
  const generateSlug = (title) => {
    if (!title) return '';
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  // Upload story cover via backend endpoint
  const uploadStoryCover = useCallback(async (file) => {
    const SERVER_URL = getServerURL();
    const formData = new FormData();
    formData.append('file', file);
    const headers = authService.getAuthHeaders();
    delete headers['Content-Type'];
    const response = await fetch(`${SERVER_URL}/generation-stories/story-cover/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `Upload failed with status ${response.status}`);
    }
    const result = await response.json();
    return result.data.url;
  }, []);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
    setNewGenerationStoryData({
        slug: '',
        title: '',
        description: '',
        gender: 'boy',
        age: [],
        language: 'english',
        comfy_flow: 'dynamic_story',
        montage_profile_key: 'dynamic-story-v1',
        montage_profile_hard_cover: '',
        montage_profile_booklet: '',
        montage_profile_pristol_express: '',
        montage_profile_booklet_express: '',
        gift_prompt: '',
        use_text_default_style: false,
        text_style_form: normalizeTextStyle({}),
      generation_provider_override: '',
      generation_model_override: '',
      regeneration_provider_override: '',
      regeneration_model_override: '',
        story_cover: ''
      });
      setGenerationStoryAgePopoverActive(false);
      setStoryCoverFile(null);
      setStoryCoverPreviewUrl(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    const loadFonts = async () => {
      setIsLoadingFonts(true);
      try {
        const result = await generationImportService.getFonts({ activeOnly: true });
        if (isMounted) {
          setAvailableFonts(Array.isArray(result?.data) ? result.data : []);
        }
      } catch (err) {
        if (isMounted) {
          setAvailableFonts([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingFonts(false);
        }
      }
    };

    const loadRegenerationModelOptions = async () => {
      try {
        const SERVER_URL = getServerURL();
        const response = await fetch(`${SERVER_URL}/settings/generation/regeneration-models`, {
          headers: authService.getAuthHeaders(),
        });
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const result = await response.json();
        const settingsDoc = result?.data || null;
        const regenOptions = extractEnabledAiModelOptions(settingsDoc || {}, 'regeneration');
        const genOptions = extractEnabledAiModelOptions(settingsDoc || {}, 'generation');
        if (isMounted) {
          setRegenerationModelOptions(regenOptions);
          setGenerationModelOptions(genOptions);
        }
      } catch (_) {
        if (isMounted) {
          setRegenerationModelOptions([]);
          setGenerationModelOptions([]);
        }
      }
    };

    loadFonts();
    loadRegenerationModelOptions();
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    montageProfilesService.getProfiles({ includeInactive: false })
      .then((profiles) => {
        if (!mounted) return;
        setAvailableMontageProfiles(profiles);
        setNewGenerationStoryData((current) => ({
          ...current,
          montage_profile_key: resolveMontageProfileKey(
            profiles,
            current,
            current.montage_profile_key,
            getDefaultMontageProfileOutputs(current)
          ),
        }));
      })
      .catch(() => {
        if (mounted) setAvailableMontageProfiles([]);
      });
    return () => {
      mounted = false;
    };
  }, [isOpen]);

  const handleCreateGenerationStory = async () => {
    if (!newGenerationStoryData.title || !newGenerationStoryData.slug) {
      return;
    }
    const layoutValidationError = validateSpreadLayoutForm(newGenerationStoryData);
    if (layoutValidationError) {
      setLayoutError(layoutValidationError);
      return;
    }
    setLayoutError('');
    setIsCreatingGenerationStory(true);
    try {
      const textDefaultStyle = newGenerationStoryData.use_text_default_style
        ? buildTextStylePayload(newGenerationStoryData.text_style_form, { useDefault: true })
        : null;

      // Upload story cover if file selected
      let storyCoverUrl = '';
      if (storyCoverFile) {
        setIsUploadingCover(true);
        try {
          storyCoverUrl = await uploadStoryCover(storyCoverFile);
        } catch (uploadErr) {
          console.error('Failed to upload story cover:', uploadErr);
          setIsUploadingCover(false);
          setIsCreatingGenerationStory(false);
          return;
        }
        setIsUploadingCover(false);
      }

      const storyData = {
        ...newGenerationStoryData,
        montage_profiles: buildMontageProfileAssignments(newGenerationStoryData, {}, availableMontageProfiles),
        generation_provider_override: normalizeProviderValue(newGenerationStoryData.generation_provider_override),
        regeneration_provider_override: normalizeProviderValue(newGenerationStoryData.regeneration_provider_override),
        age: newGenerationStoryData.age.join(', '),
        text_default_style: textDefaultStyle,
        story_cover: storyCoverUrl,
      };

      Object.assign(storyData, buildLayoutPayload(newGenerationStoryData));
      delete storyData.layout_aspect_ratio;
      delete storyData.layout_resolution;
      delete storyData.layout_wide_ratio;

      delete storyData.use_text_default_style;
      delete storyData.text_style_form;
      delete storyData.montage_profile_key;
      delete storyData.montage_profile_hard_cover;
      delete storyData.montage_profile_booklet;
      delete storyData.montage_profile_pristol_express;
      delete storyData.montage_profile_booklet_express;

      if (newGenerationStoryData.comfy_flow === 'prompt_story') {
        Object.assign(storyData, buildLayoutPayload(newGenerationStoryData));
      }
      delete storyData.layout_aspect_ratio;
      delete storyData.layout_resolution;
      delete storyData.layout_wide_ratio;

      // Import the service here to avoid circular dependencies
      const { generationStoriesService } = await import('../../services/generationStoriesService');

      await generationStoriesService.createStory(storyData);

      // Reset form
      setNewGenerationStoryData({
        slug: '',
        title: '',
        description: '',
        gender: 'boy',
        age: [],
        language: 'english',
        comfy_flow: 'dynamic_story',
        layout_mode: 'single',
        layout_aspect_ratio: '',
        layout_resolution: '2k',
        layout_wide_ratio: '',
        line_art: false,
        montage_profile_key: 'dynamic-story-v1',
        montage_profile_hard_cover: '',
        montage_profile_booklet: '',
        montage_profile_pristol_express: '',
        montage_profile_booklet_express: '',
        gift_prompt: '',
        use_text_default_style: false,
        text_style_form: normalizeTextStyle({}),
        layout_mode: 'single',
        layout_aspect_ratio: '1:1',
        layout_resolution: '1k',
        layout_wide_ratio: '',
        line_art: false,
        generation_provider_override: '',
        generation_model_override: '',
        regeneration_provider_override: '',
        regeneration_model_override: '',
        story_cover: ''
      });
      setStoryCoverFile(null);
      setStoryCoverPreviewUrl(null);

      onClose();

      // Notify parent component to refresh stories list
      if (onStoryCreated) {
        onStoryCreated();
      }
    } catch (err) {
      // handle error
      console.error('Error creating generation story:', err);
    } finally {
      setIsCreatingGenerationStory(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={<div style={{ padding: '16px' }}>{'Create New Generation Story'}</div>}
      primaryAction={{
        content: 'Create',
        onAction: handleCreateGenerationStory,
        loading: isCreatingGenerationStory,
      }}
      secondaryActions={[
        {
          content: 'Cancel',
          onAction: onClose,
        },
      ]}
      footer={<div style={{ padding: '16px' }}></div>}
    >
      <BlockStack gap="400">
        <TextField
          label="Title"
          value={newGenerationStoryData.title}
          onChange={(value) => setNewGenerationStoryData(prev => ({
            ...prev,
            title: value,
            slug: generateSlug(value)
          }))}
          placeholder="Story Title"
        />
        <TextField
          label="Slug"
          value={newGenerationStoryData.slug}
          onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, slug: value }))}
          placeholder="story-slug"
          readOnly
        />
        <TextField
          label="Description"
          value={newGenerationStoryData.description}
          onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, description: value }))}
          multiline={2}
          autoComplete="off"
          placeholder="Story description..."
        />
        <Select
          label="Gender"
          options={[
            { label: 'Boy', value: 'boy' },
            { label: 'Girl', value: 'girl' },
            { label: 'Both', value: 'both' },
          ]}
          onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, gender: value }))}
          value={newGenerationStoryData.gender}
        />
        {/* Age Selection */}
        <div>
          <Text variant="bodyMd" as="label" style={{ display: 'block', marginBottom: 4, textAlign: 'left' }}>Age</Text>
          <Popover
            active={generationStoryAgePopoverActive}
            activator={
              <Button disclosure fullWidth onClick={() => setGenerationStoryAgePopoverActive((a) => !a)} style={{
                textAlign: 'left',
                justifyContent: 'flex-start',
                paddingLeft: '12px',
                paddingRight: '12px'
              }}>
                <div style={{
                  width: '100%',
                  textAlign: 'left',
                  display: 'flex',
                  justifyContent: 'flex-start'
                }}>
                  {newGenerationStoryData.age.length > 0 ? newGenerationStoryData.age.join(', ') : 'Select Age'}
                </div>
              </Button>
            }
            onClose={() => setGenerationStoryAgePopoverActive(false)}
            fullWidth
          >
            <BlockStack gap="200" style={{ padding: 12, minWidth: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
              {ageOptions.map(range => (
                <Checkbox
                  key={range}
                  label={range}
                  checked={newGenerationStoryData.age.includes(range)}
                  onChange={(checked) => {
                    setNewGenerationStoryData(prev => ({
                      ...prev,
                      age: checked
                        ? [...prev.age, range]
                        : prev.age.filter(a => a !== range)
                    }));
                  }}
                />
              ))}
            </BlockStack>
          </Popover>
        </div>
        <Select
          label="Language"
          options={[
            { label: 'Arabic (Egyptian)', value: 'arabic-egyptian' },
            { label: 'Arabic (Saudi Arabia)', value: 'arabic-saudi' },
            { label: 'Arabic (UAE)', value: 'arabic-uae' },
            { label: 'Arabic (Qatar)', value: 'arabic-qatar' },
            { label: 'Arabic (Kuwait)', value: 'arabic-kuwait' },
            { label: 'English', value: 'english' },
            { label: 'Spanish', value: 'spanish' },
            { label: 'German', value: 'german' },
            { label: 'French', value: 'french' },
            { label: 'Italian', value: 'italian' },
          ]}
          onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, language: value }))}
          value={newGenerationStoryData.language}
        />
        <TextField
          label="Gift Prompt"
          value={newGenerationStoryData.gift_prompt}
          onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, gift_prompt: value }))}
          multiline={3}
          autoComplete="off"
          placeholder="Prompt for gift box image generation using the child's face..."
          helpText="Used by Nano Banana Pro to generate the gift box cover image (21×21 cm)"
        />
        <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
          <BlockStack gap="200">
            <Text as="p" variant="headingSm">Story Cover Image (A3 Background)</Text>
            <Text as="p" variant="bodyMd" tone="subdued">
              Upload a custom background image for the A3 cover PDF. Used as the backdrop when compositing front and back covers.
            </Text>
            {newGenerationStoryData.story_cover || storyCoverPreviewUrl ? (
              <InlineStack gap="200" blockAlign="center">
                <AuthenticatedAssetImage
                  src={storyCoverPreviewUrl || newGenerationStoryData.story_cover}
                  assetSize="thumbnail"
                  alt="Story cover"
                  style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }}
                />
                <Button
                  variant="tertiary"
                  onClick={() => {
                    setNewGenerationStoryData(prev => ({ ...prev, story_cover: '' }));
                    setStoryCoverFile(null);
                    setStoryCoverPreviewUrl(null);
                  }}
                >
                  Remove
                </Button>
              </InlineStack>
            ) : null}
            {!newGenerationStoryData.story_cover && !storyCoverFile ? (
              <DropZone
                accept="image/*"
                allowMultiple={false}
                onDrop={(files) => {
                  if (files && files.length > 0) {
                    setStoryCoverFile(files[0]);
                    setStoryCoverPreviewUrl(URL.createObjectURL(files[0]));
                  }
                }}
              >
                <DropZone.FileUpload actionHint="or drop to upload" actionTitle="Upload story cover image" />
              </DropZone>
            ) : null}
            {storyCoverFile && (
              <Text as="p" variant="bodySm" tone="success">
                {storyCoverFile.name} — will be uploaded on save
              </Text>
            )}
          </BlockStack>
        </div>
        <Select
          label="Story Flow"
          options={COMFY_FLOW_OPTIONS}
          onChange={(value) => setNewGenerationStoryData(prev => ({
            ...prev,
            comfy_flow: value,
            ...(value === 'prompt_story' ? {
              layout_aspect_ratio: prev.layout_aspect_ratio || '2:1',
              layout_resolution: prev.layout_resolution || '2k',
            } : {}),
            montage_profile_key: resolveMontageProfileKey(
              availableMontageProfiles,
              { ...prev, comfy_flow: value },
              '',
              getDefaultMontageProfileOutputs({ ...prev, comfy_flow: value })
            ),
            montage_profile_hard_cover: '',
            montage_profile_booklet: '',
            montage_profile_pristol_express: '',
            montage_profile_booklet_express: '',
          }))}
          value={newGenerationStoryData.comfy_flow}
          helpText="Image/mask flows use scene templates. Prompt-based Scenes generate scenes from per-scene prompts and support spread layout."
        />
        <Select
          label="Default montage profile"
          options={buildMontageProfileOptions(
            availableMontageProfiles,
            newGenerationStoryData,
            newGenerationStoryData.montage_profile_key,
            getDefaultMontageProfileOutputs(newGenerationStoryData)
          )}
          value={newGenerationStoryData.montage_profile_key}
          onChange={(value) => setNewGenerationStoryData(prev => ({
            ...prev,
            montage_profile_key: value,
          }))}
          helpText="Generations and orders using this story resolve their print geometry from this profile."
        />
        <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
          <BlockStack gap="300">
            <Text variant="headingSm" as="h3">Printing lane overrides</Text>
            <Text variant="bodySm" tone="subdued">
              Leave a lane on default unless that product needs different geometry.
            </Text>
            {getMontageLaneSelectors(newGenerationStoryData).map(({
              field,
              helpText,
              label,
              requiredOutputs,
            }) => (
              <Select
                key={field}
                label={label}
                options={buildMontageLaneOptions(
                  availableMontageProfiles,
                  newGenerationStoryData,
                  newGenerationStoryData.montage_profile_key,
                  newGenerationStoryData[field],
                  requiredOutputs
                )}
                value={newGenerationStoryData[field]}
                onChange={(value) => setNewGenerationStoryData(prev => ({
                  ...prev,
                  [field]: value,
                }))}
                helpText={helpText}
              />
            ))}
          </BlockStack>
        </div>

        {newGenerationStoryData.comfy_flow === 'prompt_story' ? (
        <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
          <BlockStack gap="300">
            <Text as="p" variant="headingSm">Spread layout</Text>
            <Text variant="bodySm" tone="subdued">
              Spread stories generate full two-page scenes. Coloring books use line art independently of layout.
            </Text>
            {layoutError ? <Banner tone="critical" hideOnDismiss>{layoutError}</Banner> : null}
            <Select
              label="Layout mode"
              options={[
                { label: 'Single page (default)', value: 'single' },
                { label: 'Spread (two-page)', value: 'spread' },
              ]}
              value={newGenerationStoryData.layout_mode === 'spread' ? 'spread' : 'single'}
              onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, layout_mode: value }))}
            />
            {newGenerationStoryData.layout_mode === 'spread' ? (
              <>
                <TextField
                  label="Scene aspect ratio"
                  value={newGenerationStoryData.layout_aspect_ratio}
                  onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, layout_aspect_ratio: value }))}
                  placeholder="2:1"
                  helpText="Target W:H ratio for generated spread scenes."
                  autoComplete="off"
                />
                <Select
                  label="Resolution"
                  options={[
                    { label: '1k', value: '1k' },
                    { label: '2k', value: '2k' },
                    { label: '4k', value: '4k' },
                  ]}
                  value={newGenerationStoryData.layout_resolution || '1k'}
                  onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, layout_resolution: value }))}
                />
                <TextField
                  label="Wide fallback ratio"
                  value={newGenerationStoryData.layout_wide_ratio}
                  onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, layout_wide_ratio: value }))}
                  placeholder="16:9"
                  helpText="Optional. Used if the model rejects the target ratio; leave empty for defaults."
                  autoComplete="off"
                />
              </>
            ) : null}
            <Checkbox
              label="Line art output (coloring book)"
              checked={newGenerationStoryData.line_art === true}
              onChange={(checked) => setNewGenerationStoryData(prev => ({ ...prev, line_art: checked }))}
              helpText="Generates clean line-art renders suitable for coloring books."
            />
          </BlockStack>
        </div>
        ) : null}
        <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
          <BlockStack gap="300">
            <Text as="p" variant="headingSm">Story Layout & Image Geometry</Text>
            <Text variant="bodySm" tone="subdued">
              Customize the scene aspect ratio and layout mode per story. Defaults to 1:1 Square (square_hd).
            </Text>
            <Select
              label="Layout mode"
              options={[
                { label: 'Single page (default)', value: 'single' },
                { label: 'Spread (two-page)', value: 'spread' },
              ]}
              value={newGenerationStoryData.layout_mode === 'spread' ? 'spread' : 'single'}
              onChange={(value) => setNewGenerationStoryData(prev => ({
                ...prev,
                layout_mode: value,
                montage_profile_key: resolveMontageProfileKey(
                  availableMontageProfiles,
                  { ...prev, layout_mode: value },
                  '',
                  getDefaultMontageProfileOutputs({ ...prev, layout_mode: value })
                ),
                montage_profile_hard_cover: '',
                montage_profile_booklet: '',
                montage_profile_pristol_express: '',
                montage_profile_booklet_express: '',
                layout_aspect_ratio: value === 'spread' && !prev.layout_aspect_ratio ? '2:1' : prev.layout_aspect_ratio
              }))}
            />
            <Select
              label="Aspect Ratio / Image Size"
              options={ASPECT_RATIO_PRESETS}
              value={
                ['1:1', '3:4', '4:3', '9:16', '16:9', '2:1'].includes(newGenerationStoryData.layout_aspect_ratio)
                  ? newGenerationStoryData.layout_aspect_ratio
                  : (newGenerationStoryData.layout_aspect_ratio ? 'custom' : '1:1')
              }
              onChange={(value) => setNewGenerationStoryData(prev => ({
                ...prev,
                layout_aspect_ratio: value === 'custom' ? prev.layout_aspect_ratio : value
              }))}
              helpText="FAL.AI FLUX models map 1:1 to square_hd, 3:4 to portrait_4_3, etc."
            />
            {(newGenerationStoryData.layout_aspect_ratio && !['1:1', '3:4', '4:3', '9:16', '16:9', '2:1'].includes(newGenerationStoryData.layout_aspect_ratio)) || newGenerationStoryData.layout_mode === 'spread' ? (
              <TextField
                label="Custom Scene Aspect Ratio (W:H)"
                value={newGenerationStoryData.layout_aspect_ratio}
                onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, layout_aspect_ratio: value }))}
                placeholder="2:1"
                helpText="Target W:H ratio for generated scenes."
                autoComplete="off"
              />
            ) : null}
            <Select
              label="Resolution"
              options={[
                { label: '1k', value: '1k' },
                { label: '2k', value: '2k' },
                { label: '4k', value: '4k' },
              ]}
              value={newGenerationStoryData.layout_resolution || '1k'}
              onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, layout_resolution: value }))}
            />
            {newGenerationStoryData.layout_mode === 'spread' ? (
              <TextField
                label="Wide fallback ratio"
                value={newGenerationStoryData.layout_wide_ratio}
                onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, layout_wide_ratio: value }))}
                placeholder="16:9"
                helpText="Optional. Used if the model rejects the target ratio; leave empty for defaults."
                autoComplete="off"
              />
            ) : null}
            <Checkbox
              label="Line art output (coloring book)"
              checked={newGenerationStoryData.line_art === true}
              onChange={(checked) => setNewGenerationStoryData(prev => ({ ...prev, line_art: checked }))}
              helpText="Generates clean line-art renders suitable for coloring books."
            />
          </BlockStack>
        </div>

        <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
          <BlockStack gap="300">
            <Text as="p" variant="headingSm">Generation Overrides</Text>
            <Select
              label="Generation Provider"
              options={REGEN_PROVIDER_OPTIONS}
              value={newGenerationStoryData.generation_provider_override}
              onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, generation_provider_override: value }))}
              helpText="Optional story-level override for generation routing."
            />
            <Select
              label="Generation Model"
              options={generationModelSelectOptions}
              value={newGenerationStoryData.generation_model_override}
              onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, generation_model_override: value }))}
              helpText="Values come from models with generation capability."
            />
          </BlockStack>
        </div>

        <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
          <BlockStack gap="300">
            <Text as="p" variant="headingSm">Regeneration Overrides</Text>
            <Select
              label="Regeneration Provider"
              options={REGEN_PROVIDER_OPTIONS}
              value={newGenerationStoryData.regeneration_provider_override}
              onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, regeneration_provider_override: value }))}
              helpText="Optional story-level override. Keep inherit to use global settings."
            />
            <Select
              label="Regeneration Model"
              options={regenerationModelSelectOptions}
              value={newGenerationStoryData.regeneration_model_override}
              onChange={(value) => setNewGenerationStoryData(prev => ({ ...prev, regeneration_model_override: value }))}
              helpText="Values come from models with regeneration capability."
            />
          </BlockStack>
        </div>

        <div style={{ border: '1px solid #e3e3e3', borderRadius: 8, padding: 12 }}>
          <BlockStack gap="300">
            <Text as="p" variant="headingSm">Default Text Style (for text scenes)</Text>
            <Checkbox
              label="Enable story-level default text style"
              checked={newGenerationStoryData.use_text_default_style}
              onChange={(checked) => setNewGenerationStoryData(prev => ({ ...prev, use_text_default_style: checked }))}
            />

            {newGenerationStoryData.use_text_default_style ? (
              <TextStyleEditor
                value={newGenerationStoryData.text_style_form}
                onChange={(textStyle) => setNewGenerationStoryData((previous) => ({
                  ...previous,
                  text_style_form: textStyle,
                }))}
                fontOptions={fontOptions}
                isLoadingFonts={isLoadingFonts}
              />
            ) : null}
          </BlockStack>
        </div>
      </BlockStack>
    </Modal>
  );
};

export default CreateGenerationStoryModal; 
